import api from './api'
import type {
  Document,
  DocumentListResponse,
  DocumentListParams,
  DocumentPreviewResponse,
  DocumentUploadResponse,
} from '@/schemas/document'
import type { AxiosRequestConfig, AxiosProgressEvent } from 'axios'

export interface UploadProgressCallback {
  (progress: number, filename?: string): void
}

export const documentService = {
  // Upload documents to a knowledge base
  async uploadDocuments(
    projectId: string,
    kbId: string,
    files: File[],
    onProgress?: UploadProgressCallback,
    config?: AxiosRequestConfig
  ): Promise<DocumentUploadResponse> {
    const formData = new FormData()
    files.forEach((file) => {
      formData.append('files', file)
    })

    const response = await api.post<DocumentUploadResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        },
        ...config,
      }
    )
    return response.data
  },

  // List documents in a knowledge base
  async listDocuments(
    projectId: string,
    kbId: string,
    params?: DocumentListParams,
    config?: AxiosRequestConfig
  ): Promise<DocumentListResponse> {
    const response = await api.get<DocumentListResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/documents`,
      { params, ...config }
    )
    return response.data
  },

  // Get a single document by ID
  async getDocument(
    projectId: string,
    kbId: string,
    docId: string,
    config?: AxiosRequestConfig
  ): Promise<Document> {
    const response = await api.get<Document>(
      `/projects/${projectId}/knowledge-bases/${kbId}/documents/${docId}`,
      config
    )
    return response.data
  },

  // Delete a document
  async deleteDocument(
    projectId: string,
    kbId: string,
    docId: string,
    config?: AxiosRequestConfig
  ): Promise<void> {
    await api.delete(
      `/projects/${projectId}/knowledge-bases/${kbId}/documents/${docId}`,
      config
    )
  },

  // Get document content preview (first chunks)
  async getDocumentPreview(
    projectId: string,
    kbId: string,
    docId: string,
    maxChunks?: number,
    config?: AxiosRequestConfig
  ): Promise<DocumentPreviewResponse> {
    const params = maxChunks ? { max_chunks: maxChunks } : undefined
    const response = await api.get<DocumentPreviewResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/documents/${docId}/preview`,
      { params, ...config }
    )
    return response.data
  },

  // Reprocess document (regenerate embeddings)
  async reprocessDocument(
    projectId: string,
    kbId: string,
    docId: string,
    config?: AxiosRequestConfig
  ): Promise<Document> {
    const response = await api.post<Document>(
      `/projects/${projectId}/knowledge-bases/${kbId}/documents/${docId}/reprocess`,
      null,
      config
    )
    return response.data
  },
}
